<template>
    <div
        class="participant-table overflow-x-auto"
        :class="{ 'no-raters': !showRaters, 'with-actions': remindable }"
        role="table"
        :aria-label="t('reflectionsAdmin.participantTable.tableLabel')">
        <div class="participant-grid header" role="row">
            <span role="columnheader">{{ participantHeader ?? t("reflectionsAdmin.participantTable.participant") }}</span>
            <span role="columnheader">{{ t("reflectionsAdmin.participantTable.selfReflection") }}</span>
            <span v-if="showRaters" role="columnheader">{{ t("reflectionsAdmin.participantTable.raters") }}</span>
            <span role="columnheader">{{ t("reflectionsAdmin.participantTable.report") }}</span>
            <span role="columnheader">{{ t("reflectionsAdmin.participantTable.debriefed") }}</span>
            <span v-if="remindable" role="columnheader" class="sr-only">{{ t("reflectionsAdmin.participantTable.actions") }}</span>
        </div>
        <!-- Individual 360s are one row per cycle, so a person can appear twice on a page. -->
        <div v-for="row in rows" :key="row.cycle_id ?? row.user_uuid" class="participant-grid row" role="row">
            <div class="identity flex items-center gap-2.5 min-w-0" role="cell">
                <BaseAvatar size="xs" theme="identity" :initials="row.initials" :identity-key="row.user_uuid" />
                <span class="flex flex-col min-w-0">
                    <span class="body-small text-ink truncate">{{ displayName(row) }}</span>
                    <slot name="subline" :row="row">
                        <span v-if="row.job_title" class="details text-ink-muted truncate">{{ row.job_title }}</span>
                    </slot>
                </span>
            </div>
            <span role="cell">
                <StatusChip :state="selfChip[row.self_reflection].state" size="xs">{{ selfChip[row.self_reflection].label() }}</StatusChip>
            </span>
            <span v-if="showRaters" class="details text-ink-muted whitespace-nowrap" role="cell">{{ raterCell(row) }}</span>
            <span role="cell">
                <BaseTooltip>
                    <template #trigger>
                        <span class="inline-flex" tabindex="0">
                            <StatusChip :state="reportChip[row.report].state" size="xs">
                                <template #leading><i :class="['bi', reportChip[row.report].icon, 'text-xs']" aria-hidden="true"></i></template>
                                {{ reportChip[row.report].label() }}
                            </StatusChip>
                        </span>
                    </template>
                    <template #content>{{ reportTooltip(row) }}</template>
                </BaseTooltip>
            </span>
            <!-- Self-describing text: on narrow screens the header is hidden, so a bare Yes/No would mean nothing. -->
            <span role="cell">
                <StatusChip v-if="row.debriefed_at" state="done" size="xs">
                    <template #leading><i class="bi bi-check2 text-xs" aria-hidden="true"></i></template>
                    {{ t("reflectionsAdmin.participantTable.debriefedOn", { date: formatDate(row.debriefed_at, "short") }) }}
                </StatusChip>
                <span v-else class="details text-ink-muted">{{ t("reflectionsAdmin.participantTable.notDebriefed") }}</span>
            </span>
            <span v-if="remindable" class="action justify-self-end" role="cell">
                <BaseTooltip>
                    <template #trigger>
                        <span
                            class="inline-flex"
                            :tabindex="canRemind(row) ? -1 : 0"
                            :role="canRemind(row) ? undefined : 'button'"
                            :aria-disabled="canRemind(row) ? undefined : 'true'"
                            :aria-label="canRemind(row) ? undefined : t('reflectionsAdmin.participantTable.remind', { name: displayName(row) })"
                            :aria-description="canRemind(row) ? undefined : remindTooltip(row)">
                            <BaseButton
                                theme="tertiary"
                                size="sm"
                                icon-only
                                :disabled="!canRemind(row)"
                                :aria-label="t('reflectionsAdmin.participantTable.remind', { name: displayName(row) })"
                                @click="emit('remind', row)">
                                <i class="bi bi-send" aria-hidden="true"></i>
                            </BaseButton>
                        </span>
                    </template>
                    <template #content>{{ remindTooltip(row) }}</template>
                </BaseTooltip>
            </span>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { AdminParticipantRow, ParticipantCompletionState, ParticipantReportState } from "~vue/api/generated/reflections-admin";
import BaseAvatar from "~vue/components/BaseAvatar.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import StatusChip, { type StatusChipState } from "~vue/components/StatusChip.vue";
import { formatDate } from "~vue/utils/threeSixty";
import { useI18n } from "vue-i18n";

interface Props {
    rows: AdminParticipantRow[];
    hideRaterStatus?: boolean;
    showRaters?: boolean;
    /** Replaces the "Participant" column header when the sub-line is not a job title. */
    participantHeader?: string;
    /** Adds a per-row "send reminder" action; only active campaigns offer it. */
    remindable?: boolean;
}

const { rows, hideRaterStatus = false, showRaters = true, participantHeader, remindable = false } = defineProps<Props>();
const emit = defineEmits<{ remind: [row: AdminParticipantRow] }>();
const { t } = useI18n();

const selfChip: Record<ParticipantCompletionState, { state: StatusChipState; label: () => string }> = {
    submitted: { state: "done", label: () => t("reflectionsAdmin.participantTable.selfState.submitted") },
    in_progress: { state: "pending", label: () => t("reflectionsAdmin.participantTable.selfState.inProgress") },
    not_started: { state: "neutral", label: () => t("reflectionsAdmin.participantTable.selfState.notStarted") },
    not_applicable: { state: "neutral", label: () => t("reflectionsAdmin.participantTable.selfState.notApplicable") },
    awaiting_self_reflection: {
        state: "pending",
        label: () => t("reflectionsAdmin.participantTable.selfState.awaitingSelfReflection"),
    },
};

// "Ready" is pending from the admin's side: the report exists and is waiting to be opened.
const reportChip: Record<ParticipantReportState, { state: StatusChipState; label: () => string; icon: string }> = {
    viewed: { state: "done", label: () => t("reflectionsAdmin.participantTable.reportState.viewed"), icon: "bi-check2" },
    ready: { state: "pending", label: () => t("reflectionsAdmin.participantTable.reportState.ready"), icon: "bi-file-earmark-text" },
    not_ready: { state: "neutral", label: () => t("reflectionsAdmin.participantTable.reportState.notReady"), icon: "bi-hourglass" },
};

function displayName(row: AdminParticipantRow): string {
    return row.is_former_member ? t("reflectionsAdmin.participantTable.formerMember") : row.name;
}

// Only someone who still owes a self reflection, and whose work address is still
// this org's to email, can be reminded.
function canRemind(row: AdminParticipantRow): boolean {
    return !row.is_former_member && row.self_reflection !== "submitted" && row.self_reflection !== "not_applicable";
}

function remindTooltip(row: AdminParticipantRow): string {
    if (row.is_former_member) {
        return t("reflectionsAdmin.participantTable.remindFormerMember");
    }
    if (row.self_reflection === "submitted") {
        return t("reflectionsAdmin.participantTable.remindAlreadySubmitted");
    }
    if (row.self_reflection === "not_applicable") {
        return t("reflectionsAdmin.participantTable.remindNotRequired");
    }
    return t("reflectionsAdmin.participantTable.remindHint");
}

function raterCell(row: AdminParticipantRow): string {
    if (hideRaterStatus || row.raters_responded === null || row.raters_responded === undefined) {
        return t("reflectionsAdmin.participantTable.ratersInvited", { count: row.raters_invited });
    }
    return t("reflectionsAdmin.participantTable.ratersResponded", { responded: row.raters_responded, invited: row.raters_invited });
}

function reportTooltip(row: AdminParticipantRow): string {
    switch (row.report) {
        case "viewed":
            return t("reflectionsAdmin.participantTable.viewedOn", { date: formatDate(row.report_viewed_at, "short") });
        case "ready":
            return t("reflectionsAdmin.participantTable.readyNotViewed");
        default:
            return notReadyHint(row);
    }
}

// Mirrors the cycle's own unlock rules: self-reflection only when enabled, and
// category thresholds replace the flat rater minimum when the campaign sets them.
function notReadyHint(row: AdminParticipantRow): string {
    if (!showRaters) {
        return t("reflectionsAdmin.participantTable.needsSelfReflection");
    }
    if (row.uses_category_thresholds) {
        return row.self_assess_enabled ? t("reflectionsAdmin.participantTable.needsSelfAndCategories") : t("reflectionsAdmin.participantTable.needsCategoriesOnly");
    }
    return row.self_assess_enabled
        ? t("reflectionsAdmin.participantTable.needsSelfAndCount", { count: row.min_responses })
        : t("reflectionsAdmin.participantTable.needsCountOnly", { count: row.min_responses });
}
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

@layer components {
    /* Content-sized status columns: a fixed width overflowed for wider labels and
       grew the table a horizontal scrollbar. Rows are subgrids so columns stay aligned. */
    .participant-table {
        @apply grid;
        grid-template-columns: minmax(150px, 1fr) max-content max-content max-content max-content;
    }

    .participant-table.no-raters {
        grid-template-columns: minmax(150px, 1fr) max-content max-content max-content;
    }

    .participant-table.with-actions {
        grid-template-columns: minmax(150px, 1fr) max-content max-content max-content max-content max-content;
    }

    .participant-table.no-raters.with-actions {
        grid-template-columns: minmax(150px, 1fr) max-content max-content max-content max-content;
    }

    .participant-grid {
        @apply grid grid-cols-subgrid col-span-full items-center gap-2.5 px-2;
    }

    .participant-grid.header {
        @apply scaffold-details-small text-ink-muted pb-2 border-b border-border;
    }

    .participant-grid.row {
        @apply py-2.5 border-b border-border rounded-md transition-colors hover:bg-surface-hover;
    }

    /* Narrow screens: the columns would scroll off-screen, so each row reflows into
       the person on top and their chips (and send action) wrapping underneath. */
    @media (width < theme(--breakpoint-md)) {
        .participant-table,
        .participant-table.no-raters,
        .participant-table.with-actions,
        .participant-table.no-raters.with-actions {
            grid-template-columns: 1fr;
        }

        .participant-grid.header {
            @apply sr-only;
        }

        .participant-grid.row {
            @apply flex flex-wrap gap-2;
        }

        .participant-grid.row > .identity {
            @apply basis-full;
        }

        .participant-grid.row > .action {
            @apply ml-auto;
        }
    }
}
</style>
